import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import PageHeader from '../components/PageHeader';
import toast from 'react-hot-toast';
import { ArrowLeft, CheckCircle, Clock, Info, Mail, RefreshCw, Sparkles, XCircle } from 'lucide-react';
import { datumUndZeit } from '../lib/zeit';

const TABS = [
  { key: '', label: 'Alle' },
  { key: 'neu', label: 'Neu' },
  { key: 'in_bearbeitung', label: 'In Bearbeitung' },
  { key: 'geloest', label: 'Gelöst' },
  { key: 'nicht_geloest', label: 'Nicht gelöst' },
];
const STATUS_LABEL = { neu: 'Neu', in_bearbeitung: 'In Bearbeitung', geloest: 'Gelöst', nicht_geloest: 'Nicht gelöst' };
const CAT_LABEL = { fundsache: 'Fundsache', anfrage: 'Anfrage' };   // 'support' = Standard, ohne Chip
const STATUS_COLOR = { neu: '#4b5563', in_bearbeitung: '#4b5563', geloest: '#7b1fa2', nicht_geloest: '#b91c1c' };
const fmt = (s) => datumUndZeit(s, { jahr: false, leer: '' });

export default function Tickets() {
  const [filter, setFilter] = useState('');
  const [tickets, setTickets] = useState([]);
  const [counts, setCounts] = useState({});
  const [cfg, setCfg] = useState({});
  const [sel, setSel] = useState(null);     // { ticket, messages }
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);

  const load = useCallback(() => {
    api.get('/admin/support/tickets', { params: filter ? { status: filter } : {} })
      .then(r => { setTickets(r.data.tickets); setCounts(r.data.counts); setCfg({ mail: r.data.mail_configured, imap: r.data.imap_configured }); })
      .catch(() => {});
  }, [filter]);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  const open = (id) => api.get(`/admin/support/tickets/${id}`).then(r => { setSel(r.data); setReply(''); load(); }).catch(() => {});

  const setStatus = async (status) => {
    if (!sel) return;
    try { await api.patch(`/admin/support/tickets/${sel.ticket.id}`, { status }); toast.success('Status aktualisiert'); open(sel.ticket.id); }
    catch { toast.error('Fehler'); }
  };
  const sendReply = async () => {
    if (!reply.trim() || !sel) return;
    setBusy(true);
    try { await api.post(`/admin/support/tickets/${sel.ticket.id}/reply`, { body: reply }); toast.success('Antwort per E-Mail gesendet'); open(sel.ticket.id); }
    catch (e) { toast.error(e.response?.data?.message || e.response?.data?.error || 'Senden fehlgeschlagen'); }
    finally { setBusy(false); }
  };

  // KI-Entwurf (Stufe 1, ruhend bis Schalter+Schlüssel): Label + Antwort-Vorschlag,
  // Quelle ist ausschließlich die freigegebene Kunden-FAQ — gesendet wird nur per Hand.
  const aiDraft = async () => {
    if (!sel || aiBusy) return;
    setAiBusy(true);
    try {
      const r = await api.post(`/admin/assistant/ticket-draft/${sel.ticket.id}`);
      if (r.data.escalate) toast(r.data.draft ? 'Entwurf erstellt — vor dem Senden prüfen.' : 'Eskalation empfohlen — bitte persönlich antworten.', { icon: '⚠️' });
      else toast.success('Entwurf erstellt — bitte prüfen.');
      open(sel.ticket.id);
    } catch (e) { toast.error(e.response?.data?.error || 'KI-Entwurf nicht verfügbar'); }
    finally { setAiBusy(false); }
  };

  const badge = (status) => (
    <span style={{ fontSize: 12, fontWeight: 700, color: '#fff', background: STATUS_COLOR[status] || '#4b5563', padding: '2px 8px', borderRadius: 999 }}>{STATUS_LABEL[status] || status}</span>
  );

  return (
    <div className="vg-page-pad" style={{ padding: 24 }}>
      <PageHeader
        title="Support-Tickets"
        description="Eingehende Mails an support@voogo.de — als Ticket, mit Antwort & Status."
        actions={
          <button onClick={load} style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            <RefreshCw size={14} /> Aktualisieren
          </button>
        }
      />

      {!cfg.imap && (
        <div style={{ background: '#f3f4f6', border: '1px solid #f3f4f6', color: '#4b5563', borderRadius: 10, padding: '10px 14px', fontSize: 13, marginBottom: 14 }}>
          <Info size={13} style={{ verticalAlign: -2, marginRight: 6 }} /> Posteingang inaktiv — die IMAP-Zugangsdaten (IMAP_HOST/IMAP_USER/IMAP_PASS bzw. die SMTP-Daten) fehlen in der Server-.env. Sobald gesetzt, erscheinen eingehende Mails hier automatisch.
        </div>
      )}

      {/* Status-Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => setFilter(t.key)}
            style={{ padding: '7px 13px', borderRadius: 999, border: '1px solid ' + (filter === t.key ? '#000' : '#e5e7eb'),
              background: filter === t.key ? '#000' : '#fff', color: filter === t.key ? '#fff' : '#374151', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
            {t.label}{t.key && counts[t.key] != null ? ` (${counts[t.key]})` : ''}
          </button>
        ))}
        {counts.unread > 0 && <span style={{ alignSelf: 'center', color: '#b91c1c', fontSize: 12, fontWeight: 700 }}>{counts.unread} ungelesen</span>}
      </div>

      {/* Handy: Liste ODER Detail (vg-md in index.css), Desktop unverändert nebeneinander. */}
      <div className={`vg-md${sel ? ' has-selection' : ''}`} style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
        {/* Liste */}
        <div className="vg-md-list" style={{ width: 360, flexShrink: 0, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, overflow: 'hidden' }}>
          {tickets.length === 0 && <div style={{ padding: 24, color: '#6b7280', fontSize: 14 }}>Keine Tickets.</div>}
          {tickets.map(t => (
            <div key={t.id} onClick={() => open(t.id)}
              style={{ padding: '12px 14px', borderBottom: '1px solid #f3f4f6', cursor: 'pointer',
                background: sel?.ticket?.id === t.id ? '#faf5fb' : (t.unread ? '#fafafa' : '#fff') }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                <span style={{ fontWeight: t.unread ? 800 : 600, fontSize: 14, color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.from_name || t.from_email}</span>
                <span style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  {CAT_LABEL[t.category] && <span style={{ fontSize: 12, fontWeight: 700, color: '#7b1fa2', background: '#faf5fb', border: '1px solid #e9d5f2', padding: '2px 8px', borderRadius: 999 }}>{CAT_LABEL[t.category]}</span>}
                  {badge(t.status)}
                </span>
              </div>
              <div style={{ fontSize: 13, color: '#374151', fontWeight: t.unread ? 700 : 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 2 }}>{t.subject}</div>
              <div style={{ fontSize: 12, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 2 }}>{t.last_snippet}</div>
              <div style={{ fontSize: 12, color: '#bbb', marginTop: 3 }}>{fmt(t.last_message_at)} · {t.msg_count} Nachricht(en)</div>
            </div>
          ))}
        </div>

        {/* Detail */}
        <div className="vg-md-detail" style={{ flex: 1, minWidth: 0, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, minHeight: 400 }}>
          {!sel ? (
            <div style={{ padding: 40, color: '#6b7280', textAlign: 'center' }}>Ticket auswählen.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <div style={{ padding: '16px 20px', borderBottom: '1px solid #f3f4f6' }}>
                <button type="button" className="vg-md-back" onClick={() => setSel(null)}><ArrowLeft size={15} /> Alle Tickets</button>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <h2 style={{ fontSize: 16, fontWeight: 800, color: '#111', flex: '1 1 200px', minWidth: 0, overflowWrap: 'anywhere' }}>{sel.ticket.subject}</h2>
                  <span style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    {CAT_LABEL[sel.ticket.category] && <span style={{ fontSize: 12, fontWeight: 700, color: '#7b1fa2', background: '#faf5fb', border: '1px solid #e9d5f2', padding: '2px 8px', borderRadius: 999 }}>{CAT_LABEL[sel.ticket.category]}</span>}
                    {badge(sel.ticket.status)}
                  </span>
                </div>
                <div style={{ fontSize: 13, color: '#4b5563', marginTop: 2 }}>{sel.ticket.from_name ? `${sel.ticket.from_name} · ` : ''}{sel.ticket.from_email}</div>
                <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                  <button onClick={() => setStatus('in_bearbeitung')} style={actBtn('#f3f4f6', '#4b5563')}><Clock size={14} /> In Bearbeitung</button>
                  <button onClick={() => setStatus('geloest')} style={actBtn('#faf5fb', '#7b1fa2')}><CheckCircle size={14} /> Gelöst</button>
                  <button onClick={() => setStatus('nicht_geloest')} style={actBtn('#fef2f2', '#b91c1c')}><XCircle size={14} /> Nicht gelöst</button>
                  <button onClick={aiDraft} disabled={aiBusy} style={actBtn('#faf5fb', '#9C27B0')}><Sparkles size={14} /> {aiBusy ? 'Erstelle…' : 'KI-Entwurf'}</button>
                </div>
              </div>

              {/* SUPPORT-POSTFACH-AUFRAEUMEN-001 (F7): dieselbe Mail wird im Support-Postfach aufbereitet
                  angezeigt — hier muss sie genauso aussehen. `body_display` liefert der Server NUR,
                  wenn er tatsächlich etwas entfernt hat; sonst bleibt es der unveränderte Rohtext.
                  Gerendert wird weiter ein reiner Textknoten, nie HTML. */}
              <div style={{ padding: 20, flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12, maxHeight: 460 }}>
                {sel.messages.map(m => (
                  <div key={m.id} style={{ alignSelf: m.direction === 'out' ? 'flex-end' : 'flex-start', maxWidth: '78%' }}>
                    <div style={{ background: m.direction === 'out' ? '#9C27B0' : '#f3f4f6', color: m.direction === 'out' ? '#fff' : '#111',
                      borderRadius: 12, padding: '10px 14px', fontSize: 14, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                      overflowWrap: 'anywhere' }}>{m.body_display || m.body}</div>
                    <div style={{ fontSize: 12, color: '#6b7280', marginTop: 3, textAlign: m.direction === 'out' ? 'right' : 'left' }}>
                      {m.direction === 'out' ? 'Support' : (m.from_email || '')} · {fmt(m.created_at)}
                    </div>
                  </div>
                ))}
              </div>

              {(sel.ticket.ai_draft || sel.ticket.ai_label) && (
                <div style={{ margin: '0 16px 4px', border: '1px solid #e9d5f2', borderLeft: '3px solid #9C27B0', borderRadius: 10, padding: '10px 14px', background: '#faf5fb' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: sel.ticket.ai_draft ? 6 : 0 }}>
                    <Sparkles size={13} color="#9C27B0" />
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#7b1fa2' }}>Vorschlag (KI) · {sel.ticket.ai_label || 'allgemein'}</span>
                    {sel.ticket.ai_lang && sel.ticket.ai_lang !== 'de' && (
                      <span style={{ fontSize: 12, fontWeight: 800, color: '#7b1fa2', border: '1px solid #e9d5f2', background: '#fff', padding: '1px 6px', borderRadius: 999, textTransform: 'uppercase' }}>{sel.ticket.ai_lang}</span>
                    )}
                    <span style={{ fontSize: 12, color: '#6b7280' }}>{fmt(sel.ticket.ai_at)} — prüfen, dann senden</span>
                    {sel.ticket.ai_draft && (
                      <button onClick={() => setReply(sel.ticket.ai_draft)}
                        style={{ marginLeft: 'auto', background: '#fff', border: '1px solid #9C27B0', color: '#9C27B0', borderRadius: 8, padding: '4px 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                        In Antwort übernehmen
                      </button>
                    )}
                  </div>
                  {sel.ticket.ai_draft
                    ? <div style={{ fontSize: 13, color: '#374151', whiteSpace: 'pre-wrap' }}>{sel.ticket.ai_draft}</div>
                    : <div style={{ fontSize: 12, color: '#b91c1c', fontWeight: 600 }}>Eskalation empfohlen — bitte persönlich antworten.</div>}
                  {sel.ticket.ai_draft && sel.ticket.ai_hint && (
                    <div style={{ marginTop: 8, fontSize: 12, color: '#b91c1c', fontWeight: 600 }}>Vor dem Senden prüfen: {sel.ticket.ai_hint}</div>
                  )}
                  {sel.ticket.ai_draft_de && sel.ticket.ai_lang && sel.ticket.ai_lang !== 'de' && (
                    <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px dashed #e9d5f2' }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', marginBottom: 3 }}>DEUTSCHE PRÜF-FASSUNG (wird nicht gesendet)</div>
                      <div style={{ fontSize: 13, color: '#4b5563', whiteSpace: 'pre-wrap' }}>{sel.ticket.ai_draft_de}</div>
                    </div>
                  )}
                </div>
              )}
              <div style={{ padding: 16, borderTop: '1px solid #f3f4f6' }}>
                <textarea value={reply} onChange={e => setReply(e.target.value)} placeholder="Antwort an den Absender…" rows={3}
                  style={{ width: '100%', border: '1px solid #e5e7eb', borderRadius: 10, padding: 12, fontSize: 14, resize: 'vertical', boxSizing: 'border-box' }} />
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                  <button onClick={sendReply} disabled={busy || !reply.trim()}
                    style={{ background: (busy || !reply.trim()) ? '#d1d5db' : '#000', color: '#fff', border: 'none', borderRadius: 9, padding: '10px 20px', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
                    {busy ? 'Senden…' : 'Antwort senden'}
                  </button>
                </div>
                {!cfg.mail && <div style={{ color: '#4b5563', fontSize: 12, marginTop: 6 }}>SMTP nicht konfiguriert — Antworten können nicht versendet werden.</div>}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const actBtn = (bg, color) => ({ display: 'inline-flex', alignItems: 'center', gap: 6, background: bg, color, border: '1px solid ' + color + '33', borderRadius: 8, padding: '7px 12px', fontSize: 13, fontWeight: 700, cursor: 'pointer' });
